import { publicApiClient } from '@/lib/api/client';
import type {
  AllBusinessesResponse,
  SingleBusinessResponse,
  Business,
  BusinessByHandleResponse,
  GalleryItemResponse,
  GalleryRelatedEntitiesResponse,
  Event,
  Artist
} from '@/features/clubs/types/types';

const API_PATH = '/api/public/v1';

export const clubsService = {
  /**
   * Fetches all published businesses/clubs
   * @returns Promise with all businesses data
   */
  getAllClubs: async (): Promise<Business[]> => {
    try {
      const response: AllBusinessesResponse = await publicApiClient.get(`${API_PATH}/business/all`);

      // Validate response structure and filter out invalid items
      if (!response.data || !Array.isArray(response.data)) {
        console.warn('Invalid response structure from API:', response);
        return [];
      }

      return response.data
        .filter(item => item && item.business) // Filter out invalid items
        .map(item => item.business);
    } catch (error) {
      console.error('Error fetching clubs:', error);
      throw new Error('Failed to fetch clubs');
    }
  },

  /**
   * Fetches a specific business/club by ID
   * @param businessId - The business ID to fetch
   * @returns Promise with the business data
   */
  getClubById: async (businessId: string): Promise<Business> => {
    try {
      const response: SingleBusinessResponse = await publicApiClient.get(`${API_PATH}/business/${businessId}`);

      // Validate response structure
      if (!response.data || !response.data.data || !response.data.data.business) {
        throw new Error('Invalid response structure from API');
      }

      return response.data.data.business;
    } catch (error) {
      console.error('Error fetching club by ID:', error);
      throw new Error('Failed to fetch club details');
    }
  },

  /**
   * Fetches a specific business/club by handle
   * GET /api/public/v1/business/by-handle?handle=...
   * @param handle - The business handle to fetch
   * @returns Promise with the business data
   */
  getClubByHandle: async (handle: string): Promise<Business> => {
    try {
      const response: BusinessByHandleResponse = await publicApiClient.get(`${API_PATH}/business/by-handle?handle=${encodeURIComponent(handle)}`);

      // Validate response structure
      if (!response.data || !response.data.business) {
        throw new Error('Invalid response structure from API');
      }

      return response.data.business;
    } catch (error) {
      throw new Error(`Failed to fetch club details: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Fetches gallery media for a specific business/club
   * @param businessId - The business ID to fetch gallery for
   * @returns Promise with gallery items
   */
  getClubGallery: async (businessId: string): Promise<GalleryItemResponse[]> => {
    try {
      const response = await publicApiClient.get<{ data: GalleryItemResponse[] }>(`${API_PATH}/business/${businessId}/gallery`);

      // Validate response structure
      if (!response.data || !Array.isArray(response.data)) {
        console.warn('Invalid gallery response structure:', response);
        return [];
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching gallery:', error);
      throw new Error('Failed to fetch gallery');
    }
  },

  /**
   * Fetches gallery items for a business by its handle with pagination
   * GET /api/public/v1/gallery/all-by-business-handle?handle=...&limit=...&offset=...
   * @param handle - The business handle
   * @param limit - Number of items per page (default: 50)
   * @param offset - Starting offset for pagination (default: 0)
   * @returns Promise with gallery items
   */
  getGalleryByBusinessHandle: async (
    handle: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<GalleryItemResponse[]> => {
    try {
      const response = await publicApiClient.get<{ data: Array<{ gallery_item: GalleryItemResponse }> }>(
        `${API_PATH}/gallery/all-by-business-handle?handle=${encodeURIComponent(handle)}&limit=${limit}&offset=${offset}`
      );

      // Validate response structure
      if (!response.data || !Array.isArray(response.data)) {
        console.warn('Invalid gallery response structure:', response);
        return [];
      }

      // Extract gallery_item from each wrapped object
      return response.data
        .filter(item => item && item.gallery_item)
        .map(item => item.gallery_item);
    } catch (error) {
      console.error('Error fetching gallery by handle:', error);
      throw new Error('Failed to fetch gallery');
    }
  },

  /**
   * Fetches related entities for a gallery item
   * GET /api/public/v1/gallery/:galleryItemID/related-entities
   * @param galleryItemId - The gallery item ID
   * @returns Promise with related entities
   */
  /**
   * Fetches related entities for a gallery item
   * GET /api/public/v1/gallery/:galleryItemID/related-entities
   * @param galleryItemId - The gallery item ID
   * @returns Promise with related entities
   */
  getGalleryRelatedEntities: async (
    galleryItemId: string
  ): Promise<GalleryRelatedEntitiesResponse> => {
    // DEV TESTER: Mock data for testing
    // To test, use a specific ID or uncomment the condition to always show mock data
    // if (true) {
    //   // Simulate network delay
    //   await new Promise(resolve => setTimeout(resolve, 800));

    //   return {
    //     main_entity: {
    //       id: 'mock-main-1',
    //       name: 'DJ Alexa',
    //       description: 'DJ Alexa is a DJ and producer based in New York City. She is known for her unique sound and innovative approach to electronic music.',
    //       entity_type: 'artist_entity',
    //       picture_id: null // Will show placeholder
    //     },
    //     related_content: [
    //       {
    //         id: 'mock-rel-1',
    //         name: 'DJ Phantom',
    //         description: 'Resident DJ known for deep house mixes.',
    //         entity_type: 'artist_entity',
    //         picture_id: null
    //       },
    //       {
    //         id: 'mock-rel-2',
    //         name: 'Neon Lights Party',
    //         description: 'Glow in the dark rave event.',
    //         entity_type: 'event_entity',
    //         picture_id: null
    //       },
    //       {
    //         id: 'mock-rel-3',
    //         name: 'Bass Drop Friday',
    //         description: 'Heavy bass music all night long.',
    //         entity_type: 'event_entity',
    //         picture_id: null
    //       },
    //       {
    //         id: 'mock-rel-4',
    //         name: 'Sarah Sky',
    //         description: 'Trance specialist and producer.',
    //         entity_type: 'artist_entity',
    //         picture_id: null
    //       },
    //       {
    //         id: 'mock-rel-5',
    //         name: 'Underground Vibes',
    //         description: 'Secret location party.',
    //         entity_type: 'event_entity',
    //         picture_id: null
    //       }
    //     ]
    //   };
    // }

    try {
      const response = await publicApiClient.get<GalleryRelatedEntitiesResponse>(
        `${API_PATH}/gallery/${galleryItemId}/related-entities`
      );

      // Validate response structure (response is the body)
      if (!response) {
        return { main_entity: null, related_content: [] };
      }

      return response;
    } catch (error) {
      console.error('Error fetching gallery related entities:', error);
      return { main_entity: null, related_content: [] };
    }
  },

  /**
   * Fetches all published events for a business by its handle
   * GET /api/public/v1/event/all-by-business-handle?handle=...
   * @param handle - The business handle to fetch events for
   * @returns Promise with array of events
   */
  getEventsByBusinessHandle: async (handle: string): Promise<Event[]> => {
    try {
      const response = await publicApiClient.get<{ data: Array<{ event: Event }> }>(
        `${API_PATH}/event/all-by-business-handle?handle=${encodeURIComponent(handle)}`
      );

      // Validate response structure
      if (!response.data || !Array.isArray(response.data)) {
        console.warn('Invalid events response structure:', response);
        return [];
      }

      // Extract events from wrapped objects
      return response.data
        .filter(item => item && item.event)
        .map(item => item.event);
    } catch (error) {
      console.error('Error fetching events by business handle:', error);
      return [];
    }
  },

  /**
   * Fetches all published artists for a business by its handle
   * GET /api/public/v1/artist/all-by-business-handle?handle=...
   * @param handle - The business handle to fetch artists for
   * @returns Promise with array of artists
   */
  getArtistsByBusinessHandle: async (handle: string): Promise<Artist[]> => {
    try {
      const response = await publicApiClient.get<{ data: Array<{ artist: Artist }> }>(
        `${API_PATH}/artist/all-by-business-handle?handle=${encodeURIComponent(handle)}`
      );

      // Validate response structure
      if (!response.data || !Array.isArray(response.data)) {
        console.warn('Invalid artists response structure:', response);
        return [];
      }

      // Extract artists from wrapped objects
      return response.data
        .filter(item => item && item.artist)
        .map(item => item.artist);
    } catch (error) {
      console.error('Error fetching artists by business handle:', error);
      return [];
    }
  }
};
